"use client";

import { useEffect, useState } from "react";
import { isTrustedMessageSource } from "./message-source";
import { useWidgetEnv } from "./use-widget-env";
import { useWidgetInstance } from "./widget-instance-context";

export type ThemeMode = "light" | "dark";
export type ThemePreference = ThemeMode | "auto";

/** Visitor-side overrides — sourced from the BE profile query when
 *  Profile is enabled. Honoured only when `allowed` is `true` (i.e.
 *  `themeModeEditable`); when `false`, the operator preference wins
 *  outright and the visitor's saved choice is IGNORED — matches the
 *  `WEB_PROFILE_TAB.md` contract. */
export interface VisitorThemeOpts {
  preference?: ThemePreference | null;
  allowed?: boolean;
}

/**
 * Resolves the active light/dark mode from (in priority order):
 *
 *   1. `?theme=dark|light|auto` URL param         — dashboard preview link
 *   2. `wexio:preview-theme-mode` postMessage     — dashboard live edit
 *   3. **visitor profile** (`visitor.preference`) — BE-persisted choice,
 *      honoured only when `visitor.allowed !== false`
 *   4. `operatorPreference` (config.themeMode)    — operator's saved choice
 *   5. `localStorage["wexio:theme"]`              — visitor's sticky pref
 *      (only honoured when `visitor.allowed !== false`, so the operator's
 *      "force light/dark" lock is respected)
 *   6. `prefers-color-scheme` media query         — visitor OS preference
 *
 * When `themeModeEditable === false` the visitor layers are skipped
 * entirely — operator wins outright (falls through to OS only if operator
 * is `auto`).
 *
 * Writes `data-theme="light|dark"` on the theme root so the
 * CSS-variable blocks in `globals.css` switch atomically. The theme
 * root is `env.themeRoot` when set (the React-component embed points
 * it at its Shadow DOM portal target, keeping theming self-contained),
 * otherwise `<html>` (iframe / web-component builds).
 */
export function useThemeMode(
  operatorPreference?: ThemePreference,
  visitor?: VisitorThemeOpts,
): ThemeMode {
  const [resolved, setResolved] = useState<ThemeMode>("dark");
  const env = useWidgetEnv();
  const themeRoot = env.themeRoot;
  // Host runtime override (React-component `theme` prop / web-component `theme`
  // attribute). Strong — wins over operator default + OS, like a preview link.
  const themeOverride = env.themeOverride;
  const kind = env.kind;

  const storage = useWidgetInstance().storage;
  useEffect(() => {
    const url = new URL(window.location.href);
    const urlTheme = url.searchParams.get("theme") as ThemePreference | null;
    const validOperator =
      operatorPreference && isThemePreference(operatorPreference) ? operatorPreference : null;
    const visitorAllowed = visitor?.allowed !== false;
    const validVisitor =
      visitor?.preference && isThemePreference(visitor.preference) ? visitor.preference : null;

    // The visitor's own choice, cached in localStorage so it applies
    // SYNCHRONOUSLY on reload — before the async BE preference query resolves.
    // Without this the theme flashed the operator default (e.g. light) for a
    // beat, then snapped to the visitor's saved dark once the profile query
    // answered. Skipped when the operator LOCKS the theme (`!visitorAllowed`).
    const cachedVisitor = ((): ThemePreference | null => {
      if (!visitorAllowed) return null;
      try {
        const c = storage.getItem("wexio:theme");
        return c && isThemePreference(c) ? c : null;
      } catch {
        return null;
      }
    })();

    // Resolve the active preference. URL/postMessage previews always win for
    // dashboard tooling. Then the VISITOR's own choice — the BE-persisted one,
    // or (before it loads) its localStorage cache — beats the operator's
    // default; both are skipped when the operator locks the theme. Operator
    // choice, then AUTO/OS, are the fallbacks.
    const pickPreference = (): ThemePreference => {
      // Host runtime override (in-process embeds) wins outright.
      if (themeOverride && isThemePreference(themeOverride)) return themeOverride;
      if (urlTheme && isThemePreference(urlTheme)) return urlTheme;
      if (visitorAllowed) {
        if (validVisitor && validVisitor !== "auto") return validVisitor;
        // Cache stands in for the not-yet-loaded BE preference on reload. Only
        // an explicit light/dark (never a cached "auto") so it resolves to the
        // SAME thing the loaded BE preference will — otherwise "auto" would flip.
        if (cachedVisitor && cachedVisitor !== "auto" && !validVisitor) return cachedVisitor;
      }
      if (validOperator && validOperator !== "auto") return validOperator;
      // Operator's `AUTO` (or unset) — fall through to OS.
      return "auto";
    };

    // Keep the cache in sync with the BE-persisted preference so the next
    // reload applies the right theme instantly (and a later change updates it).
    if (visitorAllowed && validVisitor) {
      try {
        storage.setItem("wexio:theme", validVisitor);
      } catch {
        /* private mode / blocked storage — the theme still resolves live */
      }
    }

    let preference: ThemePreference = pickPreference();

    const target: HTMLElement = themeRoot ?? document.documentElement;

    const apply = () => {
      const mode: ThemeMode =
        preference === "auto"
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
          : preference;
      target.dataset.theme = mode;
      setResolved(mode);
    };

    apply();

    // System pref change — only matters when `preference === "auto"`.
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onMedia = () => preference === "auto" && apply();
    media.addEventListener?.("change", onMedia);

    // Live theme override from the parent window:
    //   - `wexio:preview-theme-mode:v1` — dashboard live-edit preview.
    //   - `wexio:theme:v1`              — PRODUCTION host control. The host page
    //     switches the widget's theme at runtime (e.g. its own dark-mode toggle
    //     flipped) via `window.Wexio('theme', 'light'|'dark'|'auto')`, which the
    //     loader forwards into the iframe as this message. An explicit host call
    //     wins over operator default / OS, mirroring the preview override.
    const onMessage = (event: MessageEvent) => {
      if (!isTrustedMessageSource(event, kind)) return;
      if (!event.data || typeof event.data !== "object") return;
      if (
        event.data.type !== "wexio:preview-theme-mode:v1" &&
        event.data.type !== "wexio:theme:v1"
      ) {
        return;
      }
      const next = event.data.mode as ThemePreference | undefined;
      if (next && isThemePreference(next)) {
        preference = next;
        apply();
      }
    };
    window.addEventListener("message", onMessage);

    return () => {
      media.removeEventListener?.("change", onMedia);
      window.removeEventListener("message", onMessage);
    };
  }, [
    operatorPreference,
    themeRoot,
    themeOverride,
    kind,
    visitor?.preference,
    visitor?.allowed,
    storage,
  ]);

  return resolved;
}

function isThemePreference(v: unknown): v is ThemePreference {
  return v === "light" || v === "dark" || v === "auto";
}
